import { useEffect, useRef, useState, type CSSProperties, type FC, type ReactNode } from 'react';
import { useSubscription } from '@pages/Subscriptionsv2/hooks/useSubscriptions';
import { useFeatureVersion } from '@hooks/useFeatureVersion';
import { MENU_FEATURE_KEY } from '@utils/featureRollout/constants';
import { startPreview } from '@utils/featureRollout/preview';
import { RevertFeedbackPrompt } from './RevertFeedbackPrompt';
import { ROLE_CTA, type RevertFeedbackReason } from '@utils/featureRollout/strings';
import { V4 } from '../tokens';

// Chip DUAL del canal PULL, anclado a la línea del título de la página (NO un banner sobre el
// contenido ni una barra en una esquina muerta). Resultado del panel UX (5 expertos, 5/5):
// un CHIP corto y descubrible en el header. Self-contained: consume el hook directamente.
//
// Dos modos según la variante resuelta:
//   - CONTROL    → CTA "Probar la versión nueva" → switchTo('treatment', 'chip_pull'). Es el
//                  canal PULL: siempre disponible mientras manual_entry='available' y el comercio
//                  es elegible (hay experimento, no GA, no evacuado). NO depende de invite_surface.
//   - TREATMENT  → indicador "Versión nueva" + popover Comparar (compare_peek) / Volver
//                  (revert_commit), como antes.
//
// Visibilidad por isEligibleInvitation||variant==='treatment' (NUNCA invite_surface/state), y
// AND manual_entry!=='hidden' (GA | evacuado | opt-out del chip ocultan el canal PULL).

const CheckIcon: ReactNode = (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.6"
    aria-hidden="true"
  >
    <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const BackIcon: ReactNode = (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    aria-hidden="true"
  >
    <path d="M11 5l-7 7 7 7M4 12h16" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CompareIcon: ReactNode = (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    aria-hidden="true"
  >
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

// Icono del CTA PULL en control ("probar la versión nueva"): un destello/sparkle.
const SparkIcon: ReactNode = (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    aria-hidden="true"
  >
    <path
      d="M12 3v4M12 17v4M3 12h4M17 12h4M5.6 5.6l2.8 2.8M15.6 15.6l2.8 2.8M18.4 5.6l-2.8 2.8M8.4 15.6l-2.8 2.8"
      strokeLinecap="round"
    />
  </svg>
);

const Caret: FC<{ open: boolean }> = ({ open }) => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    aria-hidden="true"
    style={{ transition: 'transform .15s ease', transform: open ? 'rotate(180deg)' : 'none' }}
  >
    <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const chipButton: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: V4.successFaded,
  color: V4.successDark,
  border: `1px solid ${V4.successDark}22`,
  borderRadius: 999,
  padding: '6px 12px',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: 12,
  fontWeight: 700,
  whiteSpace: 'nowrap',
  lineHeight: 1,
};

// Chip del canal PULL en control: CTA "probar la versión nueva". Tono de marca (secondary)
// para diferenciarlo del chip verde de "ya estás en la nueva".
const pullChipButton: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: V4.secondaryFaded,
  color: V4.secondaryDark,
  border: `1px solid ${V4.secondaryDark}22`,
  borderRadius: 999,
  padding: '6px 12px',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: 12,
  fontWeight: 700,
  whiteSpace: 'nowrap',
  lineHeight: 1,
};

const popoverStyle: CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 8px)',
  right: 0,
  width: 'min(260px, 86vw)',
  background: V4.white,
  border: `1px solid ${V4.grayLight}`,
  borderRadius: 10,
  boxShadow: '0 12px 28px rgba(37,55,70,.15)',
  padding: 14,
  zIndex: 40,
  textAlign: 'left',
};

// Acción PRIMARIA del popover: comparar (peek). Botón con relleno (es la vía recomendada:
// mirar sin rechazar). El "volver definitivo" queda como link secundario más abajo.
const compareButton: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 7,
  width: '100%',
  background: V4.secondary,
  color: V4.white,
  border: 0,
  borderRadius: 8,
  padding: '9px 12px',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: 13,
  fontWeight: 700,
};

// Acción SECUNDARIA: volver definitivo (rechazo). Link discreto con confirmación inline para
// que no se confunda con el peek reversible.
const revertLink: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  width: '100%',
  background: 'transparent',
  color: V4.grayUltraDark,
  border: 0,
  padding: '8px 6px',
  marginTop: 8,
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: 12.5,
  fontWeight: 600,
};

// Botones de la confirmación inline del "volver definitivo".
const confirmRevertButton: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  flex: 1,
  background: V4.white,
  color: V4.secondaryDark,
  border: `1.5px solid ${V4.secondary}`,
  borderRadius: 8,
  padding: '8px 10px',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: 12.5,
  fontWeight: 700,
};

const cancelRevertButton: CSSProperties = {
  flex: 1,
  background: 'transparent',
  color: V4.grayUltraDark,
  border: `1px solid ${V4.grayLight}`,
  borderRadius: 8,
  padding: '8px 10px',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: 12.5,
  fontWeight: 600,
};

const MenuVersionStatusChip: FC = () => {
  const {
    variant,
    canRevert,
    gaForced,
    compareActive,
    manualEntry,
    isEligibleInvitation,
    switchTo,
    revert,
    submitRevertFeedback,
  } = useFeatureVersion(MENU_FEATURE_KEY);
  const { isRoot } = useSubscription();
  const [open, setOpen] = useState(false);
  const [confirmingRevert, setConfirmingRevert] = useState(false);
  const [softNotice, setSoftNotice] = useState<string | null>(null);
  // Prompt de feedback (D1): se abre tras un revert definitivo. Omitible y fire-and-forget.
  const [showFeedback, setShowFeedback] = useState(false);
  const wrapRef = useRef<HTMLSpanElement>(null);
  const chipRef = useRef<HTMLButtonElement>(null);

  // Cerrar al click-fuera y con Escape (devolviendo el foco al chip).
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
        setConfirmingRevert(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setConfirmingRevert(false);
        chipRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Devuelve true si el usuario puede cambiar la versión (root). Si no, deja el aviso suave.
  const guardRoot = (): boolean => {
    if (!isRoot) {
      setSoftNotice('Solo el administrador principal (root) puede cambiar la versión de este comercio.');
      return false;
    }
    setSoftNotice(null);
    return true;
  };

  // Envío del feedback (motivo + texto opcional). Fire-and-forget vía el hook; el prompt muestra
  // su propio agradecimiento al enviar y se cierra con Omitir/X.
  const onFeedbackSubmit = (reason: RevertFeedbackReason, text: string) => {
    submitRevertFeedback(reason, text, 'revert_feedback');
  };

  // El prompt de feedback (D1) sobrevive aunque el chip se oculte tras revertir (variant→control,
  // ya no es treatment): vive en su propio bloque, anclado al header, antes de los guards del chip.
  // El contenedor relativo TIENE ancho propio (width:min(320px,90vw)): el prompt fluye dentro y
  // queda con tamaño definido. Antes el contenedor solo envolvía un hijo `position:absolute`
  // (fuera de flujo) → caja de 0px y `right:0` anclaba contra un borde inexistente, dejando el
  // prompt invisible/mal posicionado cuando showPullChip=true (Seer #1043, finding 14509977/0).
  const feedbackNode = showFeedback ? (
    <span style={{ position: 'relative', display: 'inline-block', width: 'min(320px, 90vw)', zIndex: 50 }}>
      <RevertFeedbackPrompt onSubmit={onFeedbackSubmit} onSkip={() => setShowFeedback(false)} />
    </span>
  ) : null;

  // Visibilidad del canal PULL: el chip vive mientras manual_entry='available' (GA | evacuado |
  // opt-out del chip lo ocultan) y durante un peek la barra sticky lo cubre. Aunque el chip se
  // oculte, el prompt de feedback (si está abierto) sigue mostrándose.
  if (manualEntry === 'hidden' || gaForced || compareActive) return feedbackNode;

  // Modo CONTROL: chip CTA por ROL (canal PULL). Solo si el comercio es elegible (hay experimento
  // activo, no GA, no evacuado) Y el rol ya resolvió (Item E#4: el CTA decide por rol EN RENDER;
  // sin spinner de pantalla entera — simplemente no mostramos el chip PULL hasta resolver isRoot).
  const roleResolved = isRoot !== undefined;
  const showPullChip = variant === 'control' && isEligibleInvitation && roleResolved;
  // Modo TREATMENT: indicador "versión nueva" + popover Comparar/Volver (requiere can_revert).
  const showStatusChip = variant === 'treatment' && canRevert;
  if (!showPullChip && !showStatusChip) return feedbackNode;

  // MODO CONTROL: CTA por ROL (Item E#1, decisión EN RENDER con isRoot resuelto).
  //   - root    → adopta la versión nueva (switch DURABLE per-comercio, trigger chip_pull).
  //   - no-root → echa un vistazo (vista previa PERSONAL per-usuario, sin tocar el comercio).
  // Ya NO se bloquea al no-root con un aviso: tiene una acción real (mirar la nueva).
  const onTryNew = () => {
    if (isRoot) {
      switchTo('treatment', 'chip_pull');
      return;
    }
    startPreview(MENU_FEATURE_KEY);
  };

  // ACCIÓN PRIMARIA: comparar (peek). Mirar la anterior sin rechazar — la barra sticky aparece
  // y se puede volver a la nueva. trigger compare_peek (el backoffice no toca la membresía).
  const onCompare = () => {
    if (!guardRoot()) return;
    setOpen(false);
    switchTo('control', 'compare_peek');
  };

  // ACCIÓN SECUNDARIA: volver definitivo (rechazo). Confirmación inline antes de comprometer.
  const onRevertConfirmed = () => {
    if (!guardRoot()) return;
    setConfirmingRevert(false);
    setOpen(false);
    revert('revert_commit');
    // Abre el prompt de feedback (D1). El revert YA se ejecutó arriba: el prompt es omitible y
    // fire-and-forget, NUNCA bloquea ni revierte el cambio.
    setShowFeedback(true);
  };

  // MODO CONTROL: chip PULL simple (sin popover). CTA por rol: root deja activa la nueva
  // (durable); no-root echa un vistazo (preview personal). Si justo antes se revirtió, el prompt
  // de feedback (D1) acompaña al chip.
  if (showPullChip) {
    return (
      <>
        {feedbackNode}
        <span ref={wrapRef} style={{ position: 'relative', display: 'inline-flex' }}>
          <button ref={chipRef} type="button" style={pullChipButton} onClick={onTryNew}>
            {SparkIcon}
            {isRoot ? ROLE_CTA.rootTry : ROLE_CTA.operatorPreview}
          </button>
        </span>
      </>
    );
  }

  return (
    <span ref={wrapRef} style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        ref={chipRef}
        type="button"
        style={chipButton}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => {
          setOpen((v) => !v);
          setConfirmingRevert(false);
        }}
      >
        {CheckIcon}
        Versión nueva
        <Caret open={open} />
      </button>

      {open && (
        <span role="dialog" aria-label="Versión del menú" style={popoverStyle}>
          <span style={{ display: 'block', fontSize: 13.5, fontWeight: 700, color: V4.primary }}>
            Estás usando el menú nuevo
          </span>
          <span
            style={{ display: 'block', fontSize: 12, color: V4.grayUltraDark, margin: '4px 0 12px', lineHeight: 1.45 }}
          >
            Rediseñamos esta sección. Puedes compararla con la anterior sin perder tu lugar — y volver a la nueva cuando
            quieras.
          </span>

          {/* PRIMARIA: comparar (peek reversible). */}
          <button type="button" style={compareButton} onClick={onCompare}>
            {CompareIcon}
            Comparar con la anterior
          </button>

          {/* SECUNDARIA: volver definitivo, con confirmación inline. */}
          {!confirmingRevert ? (
            <button
              type="button"
              style={revertLink}
              onClick={() => {
                if (guardRoot()) setConfirmingRevert(true);
              }}
            >
              {BackIcon}
              Volver a la versión anterior
            </button>
          ) : (
            <span style={{ display: 'block', marginTop: 8 }}>
              <span
                style={{ display: 'block', fontSize: 11.5, color: V4.grayUltraDark, marginBottom: 8, lineHeight: 1.4 }}
              >
                ¿Volver a la versión anterior? Podrás regresar a la nueva cuando quieras.
              </span>
              <span style={{ display: 'flex', gap: 8 }}>
                <button type="button" style={confirmRevertButton} onClick={onRevertConfirmed}>
                  {BackIcon}
                  Sí, volver
                </button>
                <button type="button" style={cancelRevertButton} onClick={() => setConfirmingRevert(false)}>
                  Cancelar
                </button>
              </span>
            </span>
          )}

          {softNotice && (
            <span
              style={{
                display: 'block',
                marginTop: 10,
                padding: '8px 10px',
                borderRadius: 8,
                background: V4.warningFaded,
                color: V4.warningDark,
                fontSize: 11.5,
                fontWeight: 600,
                lineHeight: 1.4,
              }}
            >
              {softNotice}
            </span>
          )}
        </span>
      )}
    </span>
  );
};

export default MenuVersionStatusChip;
